import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Link
} from '@mui/material';
import { usePreferences } from 'hooks/usePreferences';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';
import { formatAmountForDisplayWithPreferences, toTitleCase } from 'utils/index';

const PendingInsuranceClaimReport = ({ data, onPageChange, onInvoiceClick }) => {
  const { preferences } = usePreferences();
  // this keeps track of selected rows for highlighting. We want to allow multiple selection of rows so we can compare invoices. We will store the selected invoices in an array.
  const [selectedInvoices, setSelectedInvoices] = React.useState([]);

  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Pending Insurance Claims Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  const summary = data.summary;

  const handleInvoiceClick = (invoice) => {
    if (onInvoiceClick) {
      onInvoiceClick(invoice);
    }
  }
  
  const handleRowClick = (invoice) => {
    setSelectedInvoices((prevSelected) => {
      const isSelected = prevSelected.some((inv) => inv.id === invoice.id);
      if (isSelected) {
        return prevSelected.filter((inv) => inv.id !== invoice.id);
      } else {
        return [...prevSelected, invoice];
      }
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Pending Insurance Claims Report
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Date range: {formatDateWithPreferences(data.dateFrom, preferences)} to {formatDateWithPreferences(data.dateTo, preferences)}
      </Typography>
      {data.location && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Location: {data.location}
        </Typography>
      )}
      {data.generatedAt && (
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Generated: {formatDateWithPreferences(data.generatedAt, preferences)}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Claims</Typography>
              <Typography variant="h5">{summary.totalClaims || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Claim Amount</Typography>
              <Typography variant="h5">{formatAmountForDisplayWithPreferences(summary.totalClaimAmount, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Approved Amount</Typography>
              <Typography variant="h5">{formatAmountForDisplayWithPreferences(summary.totalApprovedAmount, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>               
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Claim Date</TableCell>
              <TableCell>Claim Amount</TableCell>
              <TableCell>Claim Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.claims || []).map((claim) => (
              <TableRow key={`${claim.invoiceNumber}-${claim.invoiceDate}`} selected={selectedInvoices.some((inv) => inv.id === claim.id)} sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(claim)}>
                <TableCell>
                  <Link
                    component="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleInvoiceClick(claim);
                    }}
                  >
                    {claim.invoiceNumber}
                  </Link>
                </TableCell>
                <TableCell>{toTitleCase(claim.customerName) || 'N/A'}</TableCell>
                <TableCell>{formatDateWithPreferences(claim.invoiceDate, preferences)}</TableCell>
                <TableCell>{toTitleCase(claim.provider) || 'N/A'}</TableCell>
                <TableCell>{formatDateWithPreferences(claim.fileDate, preferences)}</TableCell>
                <TableCell>{formatAmountForDisplayWithPreferences(claim.claimAmount, preferences)}</TableCell>
                <TableCell>{claim.status}</TableCell>
              </TableRow>
            ))}
            {(!data.claims || data.claims.length === 0) && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">No claims found for the selected filters</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {data.pagination.startIndex} to {data.pagination.endIndex} of {data.pagination.totalCount} claims
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Pagination
              count={data.pagination.totalPages}
              page={data.pagination.currentPage}
              onChange={(event, page) => onPageChange && onPageChange(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PendingInsuranceClaimReport;
